import { Alert } from "@circleco/compass/components/Alert";
import { Button } from "@circleco/compass/components/Button";
import { Switch } from "@circleco/compass/components/Switch";
import { Typography } from "@circleco/compass/components/Typography";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { viewOnlyMasqueradeAs } from "@/react/api/masqueradingApi";
import { MasqueradeEntityPicker } from "@/react/components/MasqueradeBanner/MasqueradeEntityPicker";
import { MasqueradeMemberPicker } from "@/react/components/MasqueradeBanner/MasqueradeMemberPicker";
import { useMasqueradeContext } from "@/react/contexts/masqueradeContext";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useDebouncedValue } from "@/react/hooks/utils/useDebouncedValue";
import { masqueradingEntityQueries } from "@/react/lib/queries/masqueradingEntities";
import { useBackendProps } from "@/react/providers/AppContext";
import type { MasqueradingEntity } from "@/react/types/Masquerading";

const entityPickersByMode = {
  edit: MasqueradeMemberPicker,
  view: MasqueradeEntityPicker,
};

export const MasqueradeBannerV4 = ({
  isCompact = false,
}: {
  isCompact?: boolean;
}) => {
  const {
    currentCommunity,
    currentCommunityMember,
    isViewOnlyMasquerading,
    masqueradingEntity,
  } = usePunditUserContext();
  const { masquerade_referrer_url: masqueradeReferrerUrl } = useBackendProps();
  const {
    isBannerVisible,
    isEntityPickerOpen,
    setIsEntityPickerOpen,
    hideBanner,
    isCircleAiAllowed,
    shouldShowCircleAiToggle,
    setIsCircleAiAllowed,
  } = useMasqueradeContext();
  const [query, setQuery] = useState("");
  const [isNavigating, setIsNavigating] = useState(false);
  const debouncedQuery = useDebouncedValue(query.trim(), 300);
  const mode = isBannerVisible || isViewOnlyMasquerading ? "view" : "edit";
  const EntityPicker = entityPickersByMode[mode];
  const currentEntity: MasqueradingEntity | undefined = isBannerVisible
    ? undefined
    : isViewOnlyMasquerading
      ? masqueradingEntity
      : currentCommunityMember && {
          id: currentCommunityMember.id,
          type: "CommunityMember",
          name: currentCommunityMember.name,
          avatar_url: currentCommunityMember.avatar_url,
        };
  const entities = useQuery({
    ...masqueradingEntityQueries.list({
      communityId: currentCommunity?.id ?? 0,
      currentEntityKey: currentEntity
        ? `${currentEntity.type}:${currentEntity.id}`
        : "none",
      mode,
      query: debouncedQuery,
    }),
    enabled: isEntityPickerOpen && !!currentCommunity?.id,
  });
  const switchMutation = useMutation({
    mutationFn: (entity: MasqueradingEntity) =>
      viewOnlyMasqueradeAs({
        entityId: entity.id,
        entityType: entity.type,
        mode,
      }),
    onSuccess: data => {
      setIsNavigating(true);
      window.location.assign(data.masquerade_url);
    },
  });
  const handleOpenChange = (isOpen: boolean) => {
    setIsEntityPickerOpen(isOpen);
    if (!isOpen) setQuery("");
  };
  const handleSelect = (entity: MasqueradingEntity) => {
    if (entity.id === currentEntity?.id && entity.type === currentEntity.type) {
      handleOpenChange(false);
      return;
    }
    if (!switchMutation.isLoading && !isNavigating)
      switchMutation.mutate(entity);
  };

  return (
    <>
      <div
        className={classNames(
          "dark flex min-h-14 items-center justify-between gap-3 bg-secondary px-6 text-primary [--color-text-primary:var(--color-grey-50)] [--color-border-primary:var(--color-grey-700)]",
          { "flex-wrap px-4 py-2": isCompact },
        )}
        data-testid="masquerade-banner"
      >
        <div
          className={classNames("flex-1", {
            "order-last w-full flex-auto": isCompact,
            hidden: isCompact && !shouldShowCircleAiToggle,
          })}
        >
          {shouldShowCircleAiToggle && (
            <Switch
              label={t("masquerade.banner.allow_circle_ai")}
              checked={isCircleAiAllowed}
              onCheckedChange={setIsCircleAiAllowed}
              data-testid="masquerade-allow-circle-ai"
            />
          )}
        </div>
        <div
          className={classNames("flex min-w-0 items-center gap-4", {
            "flex-1 gap-2": isCompact,
          })}
        >
          <Typography variant="label-sm" color="primary" className="shrink-0">
            {mode === "view"
              ? t("view_only_masquerading.banner.title_not_selected")
              : t("masquerade.banner.title")}
          </Typography>
          <EntityPicker
            currentEntity={currentEntity}
            open={isEntityPickerOpen}
            onOpenChange={handleOpenChange}
            query={query}
            onQueryChange={setQuery}
            records={entities.data?.records ?? []}
            isLoading={entities.isFetching || query.trim() !== debouncedQuery}
            isError={entities.isError}
            isSwitching={switchMutation.isLoading || isNavigating}
            onSelect={handleSelect}
            onRetry={() => {
              void entities.refetch();
            }}
          />
        </div>
        <div
          className={classNames("flex flex-1 justify-end", {
            "flex-none": isCompact,
          })}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              hideBanner();
              if (masqueradeReferrerUrl)
                window.location.assign(masqueradeReferrerUrl);
            }}
          >
            {t("view_only_masquerading.banner.exit")}
          </Button>
        </div>
      </div>
      {switchMutation.isError && (
        <Alert
          variant="destructive"
          title={t("view_only_masquerading.picker.switch_error")}
        />
      )}
      {(switchMutation.isLoading || isNavigating) && (
        <output className="sr-only">
          {t("view_only_masquerading.picker.switching")}
        </output>
      )}
    </>
  );
};
